"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProofVerifier = void 0;
const jwt_1 = require("@ew-did-registry/jwt");
const did_ethr_resolver_1 = require("@ew-did-registry/did-ethr-resolver");
const keys_1 = require("@ew-did-registry/keys");
const did_resolver_interface_1 = require("@ew-did-registry/did-resolver-interface");
const ethers_1 = require("ethers");
const base64url_1 = __importDefault(require("base64url"));
const { arrayify, recoverAddress, keccak256, hashMessage } = ethers_1.utils;
class ProofVerifier {
    constructor(didDocument) {
        this._jwt = new jwt_1.JWT(new keys_1.Keys());
        this.verifySignature = (keys, token) => __awaiter(this, void 0, void 0, function* () {
            const results = yield Promise.all(keys.map((pubKeyField) => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (pubKeyField.publicKeyHex) {
                        const parts = pubKeyField.publicKeyHex.split('x');
                        const publickey = parts.length === 2 ? parts[1] : parts[0];
                        const decodedClaim = yield this._jwt.verify(token, publickey, {
                            algorithms: [jwt_1.Algorithms.ES256, jwt_1.Algorithms.EIP191],
                        });
                        return decodedClaim !== undefined;
                    }
                    return false;
                }
                catch (error) {
                    return false;
                }
            })));
            return keys.filter((_key, index) => results[index]);
        });
        // used to check if publicKey field in authentication refers to publicKey ID in publicKey field
        this.areLinked = (authId, pubKeyID) => {
            if (authId === pubKeyID) {
                return true;
            }
            if (authId.includes('#')) {
                return pubKeyID.split('#')[0] === authId.split('#')[0];
            }
            return false;
        };
        this._didDocument = didDocument;
    }
    /**
     * @description checks that token was issued by identity represented by
     * this verifier or his authentication delegate
     *
     * @param token
     *
     * @returns: DID of authenticated identity on successful verification or null otherwise
     */
    verifyAuthenticationProof(token) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((yield this.isIdentity(token)) ||
                (yield this.isAuthenticationDelegate(token))) {
                return this._didDocument.id;
            }
            return null;
        });
    }
    /**
     * @description Checks that token is issued by identity verification delegate
     *
     * @param token: JWT token to verify
     * @returns: DID of the authenticated identity on successful verification or null otherwise
     */
    verifyAssertionProof(token) {
        return __awaiter(this, void 0, void 0, function* () {
            if ((yield this.isIdentity(token)) ||
                (yield this.isVerificationDelegate(token))) {
                return this._didDocument.id;
            }
            return null;
        });
    }
    /**
     * @description Determines if a token was signed with an Ethereum signature
     * by the address referenced by the id of the DID Document
     * Note that JWT-compliant signatures can't be used to recover an ethereum
     */
    isIdentity(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const [encodedHeader, encodedPayload, encodedSignature] = token.split('.');
            const msg = `0x${Buffer.from(`${encodedHeader}.${encodedPayload}`).toString('hex')}`;
            const signature = base64url_1.default.decode(encodedSignature);
            const hash = arrayify(keccak256(msg));
            const claimedAddress = (0, did_ethr_resolver_1.addressOf)(this._didDocument.id);
            try {
                if (claimedAddress === recoverAddress(hash, signature)) {
                    return true;
                }
            }
            catch (_a) {
                // ignore catch
            }
            const digest = arrayify(hashMessage(hash));
            try {
                if (claimedAddress === recoverAddress(digest, signature)) {
                    return true;
                }
            }
            catch (_b) {
                // ignore catch
            }
            return false;
        });
    }
    isAuthenticationDelegate(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const validKeys = yield this.verifySignature(this.authenticationKeys(), token);
            return validKeys.length !== 0;
        });
    }
    isVerificationDelegate(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const validKeys = yield this.verifySignature(this.verificationKeys(), token);
            return validKeys.length !== 0;
        });
    }
    authenticationKeys() {
        const didPubKeys = this._didDocument.publicKey;
        if (didPubKeys.length === 0) {
            return [];
        }
        return didPubKeys.filter((key) => this.isSigAuth(key.type) ||
            this._didDocument.authentication.some((auth) => auth.publicKey &&
                this.areLinked(auth.publicKey, key.id)));
    }
    verificationKeys() {
        const didPubKeys = this._didDocument.publicKey;
        if (didPubKeys.length === 0) {
            return [];
        }
        return didPubKeys.filter((key) => this.isVeriKey(key.type));
    }
    isSigAuth(pubKeyType) {
        return pubKeyType.endsWith(did_resolver_interface_1.PubKeyType.SignatureAuthentication2018);
    }
    isVeriKey(pubKeyType) {
        return pubKeyType.endsWith(did_resolver_interface_1.PubKeyType.VerificationKey2018);
    }
}
exports.ProofVerifier = ProofVerifier;
//# sourceMappingURL=proofVerifier.js.map